// import GitHub's fetch polyfill
import "whatwg-fetch";
// import the auth store module to reference key/token header values
import auth from "@/store/modules/auth";
import {uniqueId} from "@/utils";
/*
    Vue does not provide a native AJAX client , so we've rolled our own,
    based on the emerging fetch standard: https://fetch.spec.whatwg.org/,
    with a polyfill from GitHub: https://github.github.io/fetch/

    the controller class allows for extending the utility of fetch that can
    be easily rolled back as the standard matures. we've built-out support for
    a timeout value in the options object, which triggers a race condition.

    also included is a mechanism for setting defaults for the request object
    passed to fetch. the current implementation reads from the auth store to
    set Authorization and X-CSRF-TOKEN headers on each request.
*/

// instantiate the singleton
export const RequestController = new class {

    constructor () {
        // track active requests by id.
        this.activeReqs = new Set();
    }

    fetch (url, options = {}) {
        // format the request instance - this should be reusable
        const request = url instanceof Request ? url : this.defaultRequest(url, options);
        // prepare a fetch request
        const sendRequest = () => this.send(request);

        if (options.timeout) {
            // forward a reference to the request
            const timer = () => this.setTimer(options.timeout, request);
            // this is a pretty straightforward timeout rejection pattern
            return this.race([sendRequest(), timer()]);
        }

        return sendRequest();
    }

    _fetch () {
        // return a reference to window.fetch
        if (window.fetch === undefined) {
            throw new Error("window.fetch is undefined");
        }

        return window.fetch;
    }

    race ([request, timer]) {

        const {id} = request;
        // we need to surface the id in case both timeout and abort are used
        return Object.assign(Promise.race([request, timer]), { id });
    }

    send (request) {
        // clone the outgoing request before it is used
        const recycled = request.clone();
        // get a reference to window.fetch
        const fetch = this._fetch();
        // grab a unique id
        const id = uniqueId();
        // initiate a fetch request
        const req = fetch(request).then(res => {
            // check if we're still tracking this request
            if (this.activeReqs.has(id)) {
                // proceed as usual
                return res;
            } else {
                // signal an aborted request
                return Promise.reject({
                    // send some justification
                    message: `aborted request to ${request.url}`,
                    // the request is re-usable
                    request: recycled
                });
            }
        });
        // track this request id
        this.activeReqs.add(id);
        // amend the promise with an id prop - doesn't alter functionality
        return Object.assign(req, { id });
    }

    abort (req) {
        if (!req.id) {
            throw new Error("cannot abort request");
        }
        // remove this id from our pool of tracked requests
        this.activeReqs.delete(req.id);
        // return the request
        return req;
    }

    defaultHeaders (headers) {
        // auth is retained in closure scope
        const {token} = auth.state;
        // default headers to include with every request
        const _headers = new Headers(Object.assign({
            // default to sending JSON
            "Content-Type": "application/json",
            // default to accepting JSON
            "Accept": "application/json"
        // passed headers will take precidence above defaults
        }, headers));

        if (token) {
            // insert the csrf token value
            _headers.set("X-CSRF-TOKEN", token);
        }

        return _headers;
    }

    defaultRequest (url, options) {
        // default request options - do not include timeout or other additions
        const _options = {
            // fetch defaults to GET, and so do we
            "method": options.method || "GET",
            // set the default headers
            "headers": this.defaultHeaders(options.headers),
            // default omits cookies on requests to all domains
            "credentials": options.credentials || "omit"
        };

        if (options.body) {
            // don't define a request body if one is not included in options
            _options.body = options.body;
        }

        return new Request(url, _options);
    }

    setTimer (timeout, request) {
        // a simple countdown timer
        // a timed-out request should be caught as a promise rejection
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                reject({
                    // send some justification
                    message: "timeout",
                    // the request is re-usable
                    request,
                    // the timeout value
                    timeout
                });
            }, timeout);
        });
    }
};

// export as a Vue plugin
export function install (Vue, options) {
    // make this.$request available to Vue components
    Vue.prototype.$request = RequestController;
}



// WEBPACK FOOTER //
// ./src/plugins/fetch.js